// Heal missing contractor uids on our own assignment docs.
//
// Problem: early assignment writes stamped `participantUids: [investorUid]`
// only — the contractor's uid wasn't known at assign time. Without the
// contractor's uid in that array, their `where('participantUids',
// 'array-contains', uid)` query returns zero and the assignment never shows
// up on their side.
//
// Forward fix (useAssignmentMutations) now includes team.ownerUid at write
// time. This hook covers the BACKWARD case: on investor login, scan every
// assign__ doc where investorUid === me, look up the team, and stamp
// ownerUid + any members-with-uids into participantUids if missing.
//
// Runs once per session per uid (guarded by a module-level Set). Non-
// blocking — failures log and continue.

import { useEffect, useRef } from 'react'
import { useAuth } from './useAuth'
import { useAssignmentsRealtime } from './useAssignmentsRealtime'
import { getTeam, setShared } from '../lib/db/shared'

const healedPerSession = new Set<string>()

export function useAssignmentParticipantHeal(): void {
  const { uid, isReady } = useAuth()
  const { assignments } = useAssignmentsRealtime()
  const startedRef = useRef(false)

  useEffect(() => {
    if (!uid || !isReady) return
    if (startedRef.current) return
    if (healedPerSession.has(uid)) return
    if (assignments.length === 0) return

    // Scope to assignments I OWN (I'm the investor).
    const mine = assignments.filter((a) => a.investorUid === uid && a.status === 'active')
    if (mine.length === 0) return

    startedRef.current = true
    ;(async () => {
      let patched = 0
      for (const a of mine) {
        try {
          const existing = new Set(a.participantUids || [])
          // Resolve team members to uids.
          const team = await getTeam(a.contractorCode)
          if (!team) continue
          const t = team as unknown as {
            ownerUid?: string
            members?: Array<{ uid?: string }>
            memberMeta?: Record<string, { uid?: string }>
          }
          const found = new Set<string>(existing)
          if (t.ownerUid) found.add(t.ownerUid)
          if (Array.isArray(t.members)) {
            for (const m of t.members) { if (m.uid) found.add(m.uid) }
          }
          if (t.memberMeta) {
            for (const meta of Object.values(t.memberMeta)) {
              if (meta?.uid) found.add(meta.uid)
            }
          }
          // Skip if nothing new.
          if (found.size === existing.size) continue
          const nextArr = Array.from(found)
          await setShared(a.id, { participantUids: nextArr })
          patched += 1
          console.warn(
            '[assign-heal] added contractor uids to', a.id,
            'now:', nextArr,
          )
        } catch (err) {
          console.warn('[assign-heal] failed for', a.id, err)
        }
      }
      if (patched > 0) {
        console.warn('[assign-heal] patched', patched, 'assignments')
      }
      healedPerSession.add(uid)
    })()
  }, [uid, isReady, assignments])
}
